import { useState, useRef, useEffect } from "react";

const isYoutubeEmbed = (url) => url && (url.includes("youtube.com/embed") || url.includes("player.vimeo.com"));

export function PromoStrip({ promos }) {
  // Affiches: carrousel horizontal — Spot vidéo: player intégré avec bouton volume
  if (!promos || promos.length === 0) return null;
  const posters = promos.filter((p) => p.kind === "poster");
  const videoAds = promos.filter((p) => p.kind === "video_ad");

  return (
    <div className="space-y-8">
      {posters.length > 0 && <PosterCarousel posters={posters} />}
      {videoAds.map((v) => <VideoAd key={v.id} promo={v} />)}
    </div>
  );
}

function PosterCarousel({ posters }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (posters.length <= 1) return;
    const iv = setInterval(() => setIdx((i) => (i + 1) % posters.length), 6000);
    return () => clearInterval(iv);
  }, [posters.length]);
  const p = posters[idx];
  return (
    <div className="card-amatour card-corners overflow-hidden fade-in" data-testid="poster-carousel">
      <div className="relative aspect-[16/6] bg-black/40">
        {p.link ? (
          <a href={p.link} target="_blank" rel="noreferrer"><img src={p.image_url} alt={p.title} className="w-full h-full object-cover" /></a>
        ) : (
          <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, transparent 60%, rgba(6,6,10,0.85))" }} />
        {p.title && (
          <div className="absolute bottom-4 left-6 right-6">
            <div className="font-display text-2xl" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.9)" }}>{p.title}</div>
          </div>
        )}
        {posters.length > 1 && (
          <div className="absolute bottom-4 right-6 flex gap-1.5">
            {posters.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)}
                className="w-2 h-2 rounded-full transition-all"
                style={{ background: i === idx ? "var(--amatour-gold)" : "rgba(255,255,255,0.4)", transform: i === idx ? "scale(1.4)" : "scale(1)" }}
                aria-label={`Poster ${i + 1}`} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function VideoAd({ promo }) {
  const vidRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);

  const toggleMute = () => {
    if (isYoutubeEmbed(promo.video_url)) {
      // Impossible de contrôler le volume d'un iframe YouTube sans YouTube IFrame API — on ouvre le lien
      window.open(promo.video_url.replace("/embed/", "/watch?v=").split("?")[0], "_blank");
      return;
    }
    const v = vidRef.current; if (!v) return;
    v.muted = !v.muted; setMuted(v.muted);
  };
  const togglePlay = () => {
    const v = vidRef.current; if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };

  return (
    <div className="card-amatour card-corners overflow-hidden fade-in" data-testid={`video-ad-${promo.id}`}>
      <div className="relative aspect-video bg-black">
        {isYoutubeEmbed(promo.video_url) ? (
          <iframe src={`${promo.video_url}${promo.video_url.includes("?") ? "&" : "?"}autoplay=1&mute=1&loop=1&controls=0`}
                  title={promo.title} className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen />
        ) : (
          <video ref={vidRef} src={promo.video_url} autoPlay muted={muted} loop playsInline className="w-full h-full object-cover" />
        )}
        <div className="absolute top-4 left-4">
          <div className="ribbon-gold"><i className="fa-solid fa-bullhorn" /> Spot</div>
        </div>
        <div className="absolute bottom-4 right-4 flex gap-2">
          {!isYoutubeEmbed(promo.video_url) && (
            <button onClick={togglePlay} className="w-11 h-11 rounded-full flex items-center justify-center transition"
                    style={{ background: "rgba(6,6,10,0.75)", border: "1px solid var(--amatour-line-strong)", color: "var(--amatour-gold)" }}
                    data-testid="video-ad-play">
              <i className={`fa-solid ${playing ? "fa-pause" : "fa-play"}`} />
            </button>
          )}
          <button onClick={toggleMute} className="w-11 h-11 rounded-full flex items-center justify-center transition hover:scale-110"
                  style={{ background: "rgba(6,6,10,0.75)", border: "1px solid var(--amatour-line-strong)", color: "var(--amatour-gold)" }}
                  data-testid="video-ad-volume" title={muted ? "Activer le son" : "Couper le son"}>
            <i className={`fa-solid ${muted ? "fa-volume-xmark" : "fa-volume-high"}`} />
          </button>
        </div>
        {promo.title && (
          <div className="absolute bottom-4 left-4 max-w-xs">
            <div className="italic-accent" style={{ textShadow: "0 2px 10px rgba(0,0,0,0.9)" }}>{promo.title}</div>
          </div>
        )}
      </div>
    </div>
  );
}

/* LIVE fallback : lecteur vidéo pour file d'attente quand pas de live */
export function LiveFallback({ videos, poster }) {
  const [idx, setIdx] = useState(0);
  const vidRef = useRef(null);
  const [muted, setMuted] = useState(true);

  if (!videos || videos.length === 0) return null;
  const current = videos[idx];
  const next = () => setIdx((i) => (i + 1) % videos.length);
  const prev = () => setIdx((i) => (i - 1 + videos.length) % videos.length);

  const toggleMute = () => {
    if (isYoutubeEmbed(current.video_url)) return;
    const v = vidRef.current; if (!v) return;
    v.muted = !v.muted; setMuted(v.muted);
  };

  return (
    <div className="relative w-full h-full bg-black rounded-xl overflow-hidden" data-testid="live-fallback">
      {isYoutubeEmbed(current.video_url) ? (
        <iframe key={current.id} src={`${current.video_url}${current.video_url.includes("?") ? "&" : "?"}autoplay=1&mute=1`}
                title={current.title} className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen />
      ) : (
        <video key={current.id} ref={vidRef} src={current.video_url} autoPlay muted={muted} loop playsInline
               className="w-full h-full object-cover" poster={poster} />
      )}
      <div className="absolute top-4 left-4 flex items-center gap-2">
        <div className="chip-red chip"><i className="fa-solid fa-circle" style={{ fontSize: 8 }} /> En attente du live</div>
        <div className="tag">{idx + 1} / {videos.length}</div>
      </div>
      <div className="absolute bottom-4 right-4 flex gap-2">
        {videos.length > 1 && <>
          <button onClick={prev} className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "rgba(6,6,10,0.75)", border: "1px solid var(--amatour-line-strong)", color: "var(--amatour-gold)" }} data-testid="live-prev"><i className="fa-solid fa-backward-step" /></button>
          <button onClick={next} className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "rgba(6,6,10,0.75)", border: "1px solid var(--amatour-line-strong)", color: "var(--amatour-gold)" }} data-testid="live-next"><i className="fa-solid fa-forward-step" /></button>
        </>}
        {!isYoutubeEmbed(current.video_url) && (
          <button onClick={toggleMute} className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "rgba(6,6,10,0.75)", border: "1px solid var(--amatour-line-strong)", color: "var(--amatour-gold)" }} data-testid="live-volume">
            <i className={`fa-solid ${muted ? "fa-volume-xmark" : "fa-volume-high"}`} />
          </button>
        )}
      </div>
      {current.title && (
        <div className="absolute bottom-4 left-4 max-w-md">
          <div className="font-display text-lg" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.9)" }}>{current.title}</div>
        </div>
      )}
    </div>
  );
}
